import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Separator } from "../ui/separator";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../ui/select";
import { projects, wings, bhkTypes, formatCurrency } from "./constants";

const PropertyDetailsStep = ({ formData, onInputChange, onSelectChange, priceCalc }) => {
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="project">Project *</Label>
          <Select value={formData.project} onValueChange={(v) => onSelectChange("project", v)}>
            <SelectTrigger data-testid="project-select"><SelectValue placeholder="Select project" /></SelectTrigger>
            <SelectContent>
              {projects.map((p) => (
                <SelectItem key={p.name} value={p.name}>{p.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label htmlFor="tower">Wing *</Label>
          <Select value={formData.tower} onValueChange={(v) => onSelectChange("tower", v)}>
            <SelectTrigger data-testid="wing-select"><SelectValue placeholder="Select wing" /></SelectTrigger>
            <SelectContent>
              {wings.map((w) => (
                <SelectItem key={w} value={w}>{w}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label htmlFor="unit_number">Unit Number *</Label>
          <Input id="unit_number" name="unit_number" value={formData.unit_number} onChange={onInputChange} placeholder="e.g., 0701" required data-testid="unit-number-input" />
        </div>
        <div className="space-y-2">
          <Label htmlFor="bhk_type">BHK Type *</Label>
          <Select value={formData.bhk_type} onValueChange={(v) => onSelectChange("bhk_type", v)}>
            <SelectTrigger data-testid="bhk-type-select"><SelectValue placeholder="Select BHK" /></SelectTrigger>
            <SelectContent>
              {bhkTypes.map((bhk) => (
                <SelectItem key={bhk} value={bhk}>{bhk}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label htmlFor="floor">Floor Number</Label>
          <Input id="floor" name="floor" type="number" value={formData.floor} onChange={onInputChange} placeholder="e.g., 7" data-testid="floor-input" />
        </div>
        <div className="space-y-2">
          <Label htmlFor="saleable_area">Total Saleable Area (sq.ft) *</Label>
          <Input id="saleable_area" name="saleable_area" type="number" value={formData.saleable_area} onChange={onInputChange} placeholder="e.g., 1630" required data-testid="saleable-area-input" />
        </div>
        <div className="space-y-2">
          <Label htmlFor="rate_per_sqft">Rate per sq.ft (&#8377;) *</Label>
          <Input id="rate_per_sqft" name="rate_per_sqft" type="number" value={formData.rate_per_sqft} onChange={onInputChange} placeholder="e.g., 6600" required data-testid="rate-input" />
        </div>
        <div className="space-y-2">
          <Label>Car Parking Charges</Label>
          <div className="px-3 py-2 rounded-md border bg-slate-50 text-slate-700 font-medium" data-testid="car-parking-fixed">
            &#8377; 2,00,000 (fixed)
          </div>
          <p className="text-xs text-slate-500">Fixed charge added to every flat — not multiplied by slot count.</p>
        </div>
        <div className="space-y-2">
          <Label htmlFor="club_house_charges">Amenities Charges (&#8377;)</Label>
          <Input id="club_house_charges" name="club_house_charges" type="number" min="0" value={formData.club_house_charges} onChange={onInputChange} placeholder="200000" data-testid="amenities-input" />
          <p className="text-xs text-slate-500">Default &#8377;2,00,000 — editable</p>
        </div>
        <div className="space-y-2 col-span-2">
          <Label htmlFor="additional_charges">Additional Charges (&#8377;) - Manual Entry</Label>
          <Input id="additional_charges" name="additional_charges" type="number" min="0" value={formData.additional_charges} onChange={onInputChange} placeholder="Enter any additional charges" />
          <p className="text-xs text-slate-500">Enter any other extra charges</p>
        </div>
      </div>

      {/* Live Price Calculator */}
      {formData.saleable_area && formData.rate_per_sqft && (
        <div className="mt-6 p-4 bg-primary/5 border border-primary/20 rounded-lg">
          <h3 className="font-semibold text-slate-700 mb-3">Price Calculation Preview</h3>
          <div className="grid grid-cols-2 gap-2 text-sm">
            <span className="text-slate-600">Base Price ({formData.saleable_area} sq.ft x &#8377;{formData.rate_per_sqft}):</span>
            <span className="font-medium text-right">{formatCurrency(priceCalc.basePrice)}</span>
            <span className="text-slate-600">Car Parking (fixed):</span>
            <span className="font-medium text-right">{formatCurrency(priceCalc.parkingCharges)}</span>
            <span className="text-slate-600">Amenities:</span>
            <span className="font-medium text-right">{formatCurrency(priceCalc.amenities)}</span>
            {priceCalc.additionalCharges > 0 && (
              <>
                <span className="text-slate-600">Additional Charges:</span>
                <span className="font-medium text-right">{formatCurrency(priceCalc.additionalCharges)}</span>
              </>
            )}
            <span className="text-slate-600">Labour Cess (0.70%):</span>
            <span className="font-medium text-right">{formatCurrency(priceCalc.labourCess)}</span>
            <span className="text-slate-600">GST (5%):</span>
            <span className="font-medium text-right">{formatCurrency(priceCalc.gst)}</span>
            <Separator className="col-span-2 my-2" />
            <span className="font-semibold text-primary">Total Flat Value:</span>
            <span className="font-bold text-primary text-right text-lg">{formatCurrency(priceCalc.total)}</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default PropertyDetailsStep;
